// src/apiBase.js
// FINAL CLOUDFLARE + LOCALHOST READY - COPY/PASTE

function safeTrim(v) {
  return (v ?? "").toString().trim();
}

function safeLower(v) {
  return safeTrim(v).toLowerCase();
}

function normalize(url) {
  return safeTrim(url).replace(/\/+$/, "");
}

function getQueryParam(name) {
  try {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getCookie(name) {
  try {
    if (typeof document === "undefined") return "";
    const rx = new RegExp(`(?:^|; )${escapeRegex(name)}=([^;]*)`);
    const m = document.cookie.match(rx);
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
}

const HAS_WINDOW = typeof window !== "undefined";

/* ---------------- API base ---------------- */

const DEV_API = "http://127.0.0.1:8000";
const PROD_API = "https://api.bamboome.me";

function isLocalHost() {
  if (!HAS_WINDOW) return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

function getEnvApiBase() {
  try {
    return normalize(import.meta.env.VITE_API_BASE || "");
  } catch {
    return "";
  }
}

function getQueryApiBase() {
  return normalize(getQueryParam("apiBase"));
}

export function getApiBase() {
  const queryApi = getQueryApiBase();
  if (queryApi) return queryApi;

  const envApi = getEnvApiBase();
  if (envApi) return envApi;

  if (isLocalHost()) return DEV_API;

  return PROD_API;
}

export const API = normalize(getApiBase());

export const GLOBAL_API = API;

export const CITY_API = {
  central: API,
  jounieh: API,
  tripoli: API,
  batroun: API,
  zgharta: API,
  ehden: API,
};

export const SERVICE_API = {
  birthdays: API,
  loyalty: API,
};

export const BIRTHDAYS_API = normalize(SERVICE_API.birthdays);
export const LOYALTY_API = normalize(SERVICE_API.loyalty);

/* ---------------- Branch handling ---------------- */

export const VALID_BRANCHES = [
  "jounieh",
  "tripoli",
  "batroun",
  "zgharta",
  "ehden",
];

const BRANCH_STORAGE_KEY = "app:activeBranch:v3";
const LEGACY_BRANCH_STORAGE_KEYS = ["app:activeBranch:v2", "app:activeBranch:v1"];

let MEM_BRANCH = "";
let BRANCH_LOCK = "";

function normalizeBranch(branch) {
  const b = safeLower(branch);
  return VALID_BRANCHES.includes(b) ? b : "";
}

function readStoredBranch() {
  if (!HAS_WINDOW) return "";
  try {
    return normalizeBranch(window.sessionStorage.getItem(BRANCH_STORAGE_KEY) || "");
  } catch {
    return "";
  }
}

function writeStoredBranch(branch) {
  if (!HAS_WINDOW) return;
  try {
    const b = normalizeBranch(branch);
    if (b) window.sessionStorage.setItem(BRANCH_STORAGE_KEY, b);
    else window.sessionStorage.removeItem(BRANCH_STORAGE_KEY);
  } catch {}
}

function branchFromQuery() {
  if (!HAS_WINDOW) return "";
  return normalizeBranch(getQueryParam("branch"));
}

export function getActiveBranch() {
  // Active selection is authoritative. BRANCH_LOCK is only a UI hint for
  // single-branch users; it must never override a new explicit selection.
  const b = normalizeBranch(
    MEM_BRANCH || readStoredBranch() || branchFromQuery() || BRANCH_LOCK || ""
  );
  if (b && !MEM_BRANCH) MEM_BRANCH = b;
  return b;
}

export const BRANCH = "";

export function getBranchLock() {
  return normalizeBranch(BRANCH_LOCK);
}

export function isBranchLocked() {
  return Boolean(getBranchLock());
}

function broadcastBranchChange() {
  if (!HAS_WINDOW) return;
  try {
    window.dispatchEvent(new Event("bamboo:branch"));
    window.dispatchEvent(new Event("app:branch"));
  } catch {}
}

export function lockBranch(branch, opts = {}) {
  if (!HAS_WINDOW) return "";

  const b = normalizeBranch(branch);
  if (!b) return "";

  const already = normalizeBranch(BRANCH_LOCK);
  if (already === b) {
    MEM_BRANCH = b;
    broadcastBranchChange();
    return BRANCH_LOCK;
  }

  BRANCH_LOCK = b;
  MEM_BRANCH = b;
  writeStoredBranch(b);
  broadcastBranchChange();

  if (opts.reload === true) window.location.reload();
  return BRANCH_LOCK;
}

export function unlockBranch(opts = {}) {
  if (!HAS_WINDOW) return;

  BRANCH_LOCK = "";
  broadcastBranchChange();

  if (opts.reload === true) window.location.reload();
}

export function setBranch(branch, opts = {}) {
  if (!HAS_WINDOW) return "";

  const b = normalizeBranch(branch);

  // IMPORTANT:
  // Never ignore an explicit branch change in the browser. Authorization is
  // enforced by FastAPI using the authenticated user's allowed branches.
  // BRANCH_LOCK remains only as a UI/state hint for one-branch staff.
  MEM_BRANCH = b || "";
  writeStoredBranch(MEM_BRANCH);

  // If the caller is deliberately forcing a branch while a UI lock exists,
  // move the lock with the active branch instead of leaving a stale lock.
  if (BRANCH_LOCK && opts.force === true) {
    BRANCH_LOCK = MEM_BRANCH;
  }

  broadcastBranchChange();

  if (opts.reload === true) window.location.reload();
  return MEM_BRANCH;
}

/* ---------------- Request helpers ---------------- */

function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

function isArrayBuffer(value) {
  return typeof ArrayBuffer !== "undefined" && value instanceof ArrayBuffer;
}

function isURLSearchParams(value) {
  return typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams;
}

function isPlainObject(value) {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !isFormData(value) &&
    !isBlob(value) &&
    !isArrayBuffer(value) &&
    !isURLSearchParams(value)
  );
}

function buildBodyAndHeaders(init = {}) {
  const headers = new Headers(init.headers || {});
  let body = init.body;

  if (body != null && isPlainObject(body)) {
    body = JSON.stringify(body);
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  return { headers, body };
}

function safeUpper(v) {
  return safeTrim(v).toUpperCase();
}

function withDefaultHeaders(init = {}) {
  const prepared = buildBodyAndHeaders(init);
  const headers = prepared.headers;
  const body = prepared.body;

  const rawMethod = safeUpper(init.method || "");
  const hasBody = body !== undefined && body !== null;
  const method = rawMethod || (hasBody ? "POST" : "GET");

  const activeBranch = getActiveBranch();
  if (activeBranch) {
    if (!headers.has("x-branch")) headers.set("x-branch", activeBranch);
    if (!headers.has("X-Branch")) headers.set("X-Branch", activeBranch);
  }

  const csrf =
    getCookie("csrf") ||
    getCookie("csrf_token") ||
    getCookie("XSRF-TOKEN") ||
    "";

  if (csrf && !headers.has("x-csrf-token")) {
    headers.set("x-csrf-token", csrf);
  }

  return {
    ...init,
    method,
    headers,
    body,
    credentials: init.credentials ?? "include",
  };
}

function buildUrl(path) {
  const p = String(path || "");
  if (!p) return API;
  if (/^https?:\/\//i.test(p)) return p;
  return `${API}${p.startsWith("/") ? p : `/${p}`}`;
}

async function readResponseBody(res) {
  const ct = res.headers.get("Content-Type") || "";
  const isJson = ct.includes("application/json");

  if (res.status === 204) {
    return { isJson, body: null };
  }

  if (isJson) {
    const text = await res.text().catch(() => "");
    if (!text) return { isJson: true, body: null };

    try {
      return { isJson: true, body: JSON.parse(text) };
    } catch {
      return { isJson: true, body: null };
    }
  }

  const text = await res.text().catch(() => "");
  return { isJson: false, body: text };
}

function extractErrorMessage(body, fallbackStatusText) {
  if (!body) return fallbackStatusText || "Request failed";

  if (typeof body === "string") {
    return body.trim() || fallbackStatusText || "Request failed";
  }

  if (typeof body === "object") {
    return (
      body.error ||
      body.detail ||
      body.message ||
      body.msg ||
      fallbackStatusText ||
      "Request failed"
    );
  }

  return fallbackStatusText || "Request failed";
}

/* ---------------- Main fetch helpers ---------------- */

export const apiFetch = (path, init = {}) => {
  const url = buildUrl(path);
  return fetch(url, withDefaultHeaders(init));
};

export const apiJSON = async (path, init = {}) => {
  const res = await apiFetch(path, init);
  const parsed = await readResponseBody(res);

  if (!res.ok) {
    const msg = extractErrorMessage(
      parsed.body,
      `HTTP ${res.status} ${res.statusText}`
    );
    const err = new Error(msg);
    err.status = res.status;
    err.payload = parsed.body;
    err.response = res;
    throw err;
  }

  return parsed.body;
};

/* ---------------- Service helpers ---------------- */

function toAbs(serviceBase, path) {
  const p = String(path || "");
  if (!p) return serviceBase;
  return /^https?:\/\//i.test(p)
    ? p
    : `${serviceBase}${p.startsWith("/") ? p : `/${p}`}`;
}

export function apiFetchService(service, path, init = {}) {
  const base =
    service === "birthdays"
      ? BIRTHDAYS_API
      : service === "loyalty"
      ? LOYALTY_API
      : API;

  return fetch(toAbs(base, path), withDefaultHeaders(init));
}

export async function apiJSONService(service, path, init = {}) {
  const res = await apiFetchService(service, path, init);
  const parsed = await readResponseBody(res);

  if (!res.ok) {
    const msg = extractErrorMessage(
      parsed.body,
      `HTTP ${res.status} ${res.statusText}`
    );
    const err = new Error(msg);
    err.status = res.status;
    err.payload = parsed.body;
    err.response = res;
    throw err;
  }

  return parsed.body;
}

export function setApiBase(_url) {
  console.warn("[apiBase] setApiBase ignored. Use VITE_API_BASE or ?apiBase=");
}

/* ---------------- Notification Center helpers ---------------- */

export async function getNotifications({
  limit = 30,
  unreadOnly = false,
  branch = "",
} = {}) {
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  q.set("unread_only", unreadOnly ? "1" : "0");
  if (safeTrim(branch)) q.set("branch", safeTrim(branch));
  return apiJSON(`/notification-center/list?${q.toString()}`);
}

export async function getUnreadNotificationCount({ branch = "" } = {}) {
  const q = new URLSearchParams();
  if (safeTrim(branch)) q.set("branch", safeTrim(branch));
  return apiJSON(
    `/notification-center/unread-count${q.toString() ? `?${q.toString()}` : ""}`
  );
}

export async function markNotificationRead(notificationId) {
  if (!notificationId) throw new Error("notificationId is required");
  return apiJSON(`/notification-center/${notificationId}/read`, {
    method: "POST",
  });
}

export async function markAllNotificationsRead({ branch = "" } = {}) {
  const q = new URLSearchParams();
  if (safeTrim(branch)) q.set("branch", safeTrim(branch));
  return apiJSON(
    `/notification-center/read-all${q.toString() ? `?${q.toString()}` : ""}`,
    {
      method: "POST",
    }
  );
}

export async function deleteNotification(notificationId) {
  if (!notificationId) throw new Error("notificationId is required");
  return apiJSON(`/notification-center/${notificationId}`, {
    method: "DELETE",
  });
}

export async function createTestNotification(payload = {}) {
  return apiJSON("/notification-center/test", {
    method: "POST",
    body: payload,
  });
}

export async function createAdminNotification(payload = {}) {
  return apiJSON("/notification-center/admin-create", {
    method: "POST",
    body: payload,
  });
}

/* ---------------- ERP aliases ---------------- */

export const ERP_API = API;
export const erpFetch = apiFetch;
export const erpJSON = apiJSON;

/* ---------------- Debug helpers ---------------- */

if (HAS_WINDOW) {
  console.log("[apiBase] API:", API);
  console.log(
    "[apiBase] BRANCH:",
    getActiveBranch() || "(none)",
    "| LOCK:",
    getBranchLock() || "(none)"
  );

  window.__API_BASE__ = API;
  window.__BRANCH__ = {
    getActiveBranch,
    getBranchLock,
    isBranchLocked,
    setBranch,
    lockBranch,
    unlockBranch,
  };
}

export default API;